import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {EventItem, League, LeagueEvents, LeagueEventType, Lookups, MultiLangField, Race, TOAST_STATE} from "@model";
import {NgbModal} from "@ng-bootstrap/ng-bootstrap";
import {
  AbstractComponent,
  ConfirmationModalComponent,
  EventShortNameModalComponent,
  EventTemplateModalComponent,
  LeagueEventTableType,
  TreeSelectItem,
  TreeSelectModal
} from "@components";
import {ActivatedRoute, Router} from "@angular/router";
import {TranslocoService} from "@ngneat/transloco";
import {
  AuthenticationService,
  EventService,
  LeagueEventsService,
  LoaderService,
  RequestService,
  ToastService
} from "@services";

@Component({
  selector: 'app-league-events',
  templateUrl: './league-events.component.html',
  styleUrl: './league-events.component.scss',
  standalone: false,
})
export class LeagueEventsComponent extends AbstractComponent implements OnInit, OnDestroy{
  @Input() disableEditing: boolean = true;
  @Input() league: League | undefined;
  @Input() leagueEvents: LeagueEvents | undefined;
  @Input({required: true}) languages: Lookups[] = [];
  @Input() sports: Lookups[] = [];
  @Input() countries: Lookups[] = [];
  @Input() eventValidityMap: Map<number, boolean> = new Map<number, boolean>()
  @Output() onLeagueEventsChange: EventEmitter<LeagueEvents> = new EventEmitter<LeagueEvents>();
  eventsCollapsed: boolean = false;
  pendingCollapsed: boolean = false;
  templateCollapsed: boolean = false;

  constructor(
      private route: ActivatedRoute,
      public translocoService: TranslocoService,
      private eventService: EventService,
      private leagueEventsService: LeagueEventsService,
      private modalService: NgbModal,
      private authService: AuthenticationService,
      private toastService: ToastService,
      private loaderService: LoaderService,
      private router: Router,
      private requestService: RequestService,
    ) {
      super();
    }

  ngOnInit() {
  }

  openEventsForLeagueList() {
    this.leagueEventsService.availableEventsForLeague(this.league?.id).subscribe((eventItems: EventItem[]) => {
      if (!eventItems?.length) {
        this.toastService.showToast(TOAST_STATE.danger, this.translocoService.translate("league.details.events.available.empty"));
        return;
      };

      const modalRef = this.modalService.open(TreeSelectModal, { centered: true, size: 'lg', windowClass: 'tree-select-modal' });
      modalRef.componentInstance.items = eventItems.map(this.mapEventToModalItem);
      modalRef.componentInstance.title = "league.details.list.of.events";
      modalRef.componentInstance.tableColumnName = "league.details.list.of.events.event.name";
      modalRef.componentInstance.subItemCheckboxEnabled = true;
      modalRef.componentInstance.disabled = this.disableEditing;

      modalRef.result.then((modalSelectedMap: Map<number, Set<number>>) => {
        if (!this.league || this.disableEditing) return;
        this.loaderService.startLoading();
        this.leagueEventsService.addEventsToLeague(this.league!.id!, this.mapModalSelectionToEvents(modalSelectedMap),
            () => {
              this.requestService.fetchNewRequests.next(true);
              this.refreshLeagueEvents("league.details.list.of.events.add.success");
            },
            () => this.handleError("league.details.list.of.events.add.error")
        );
      }, () => {});
    });
  }

  openCreateEventTemplate(templateEvent?: EventItem) {
    if (!this.league?.id) return;

    const modalRef = this.modalService.open(EventTemplateModalComponent, { centered: true, size: 'xl' });
    modalRef.componentInstance.title = templateEvent ? "league.events.edit.template.title" : "league.events.add.template.title";
    modalRef.componentInstance.templateEvent = templateEvent;
    modalRef.componentInstance.league = this.league;
    modalRef.componentInstance.sports = this.sports;
    modalRef.componentInstance.countries = this.countries;

    modalRef.componentInstance.onSave.subscribe((eventItem: EventItem) => {
      modalRef.close();
      if (!this.league || this.disableEditing) return;
      this.loaderService.startLoading();

      const onSuccess = () => this.refreshLeagueEvents("league.events.create.template.success");
      const onError = () => this.handleError("league.events.create.template.error");

      templateEvent?.id
          ? this.leagueEventsService.editEventTemplate(this.league!.id!, eventItem, onSuccess, onError)
          : this.leagueEventsService.addEventTemplate(this.league!.id!, eventItem, onSuccess, onError);
    });
  }


  private refreshLeagueEvents(successKey: string) {
    this.leagueEventsService.getLeagueEventsProtected(this.league!.id!).subscribe((value: LeagueEvents | undefined) => {
      this.leagueEvents = value;
      this.loaderService.dismissLoading();
      this.toastService.showToast(TOAST_STATE.success, this.translocoService.translate(successKey));
      this.onLeagueEventsChange.emit(value);
    });
  }

  private handleError(errorKey: string) {
    this.loaderService.dismissLoading();
    this.toastService.showToast(TOAST_STATE.danger, this.translocoService.translate(errorKey));
  }

  private mapEventToModalItem = (event: EventItem): TreeSelectItem => {
    const disabled = event.pendingLeague || this.disableEditing;
    return {
      id: event.id!,
      name: event.name,
      description: event.organiser?.name,
      selected: event.isInLeague || event.pendingLeague,
      disabled,
      subItems: (event.racesList ?? []).map(r => ({
        id: r.id!,
        name: r.name,
        selected: r.pendingLeague || r.isInLeague,
        disabled,
        visible: true
      } as TreeSelectItem)),
      visible: true,
      tooltipMessage: event.pendingLeague ? 'request.pending.league.tooltip' : undefined
    } as TreeSelectItem;
  };


  private mapModalSelectionToEvents(modalSelectedMap: Map<number, Set<number>>): EventItem[] {
    const events: EventItem[] = [];
    for (const [eventId, raceIds] of modalSelectedMap.entries()) {
      const event = new EventItem();
      event.id = eventId;
      event.isInLeague = true;

      event.racesList = Array.from(raceIds).map(raceId => {
        const race = new Race();
        race.id = raceId;
        race.isInLeague = true;
        return race;
      });

      events.push(event);
    }
    return events;
  }

  openDeleteDialog(value: { id: number, isRace: boolean }, isTemplate: boolean) {
    if (this.disableEditing) return;
    const { id, isRace } = value;

    const modalRef = this.modalService.open(ConfirmationModalComponent, { centered: true });
    modalRef.componentInstance.title = this.translocoService.translate(isRace ? 'league.details.list.of.events.delete.race.modal.title' : 'league.details.list.of.events.delete.event.modal.title');
    modalRef.componentInstance.text = this.translocoService.translate(isRace ? 'league.details.list.of.events.delete.race.modal.text' : 'league.details.list.of.events.delete.event.modal.text');

    modalRef.result.then(() => {
      this.loaderService.startLoading();
      const successKey = isRace ? 'league.details.list.of.events.delete.race.success' : 'league.details.list.of.events.delete.event.success';
      const errorKey = isRace ? 'league.details.list.of.events.delete.race.error' : 'league.details.list.of.events.delete.event.error';

      const onSuccess = () => this.refreshLeagueEvents(successKey);
      const onError = () => this.handleError(errorKey);

      isRace
          ? this.leagueEventsService.removeRaceFromLeague(id, this.league!.id!, onSuccess, onError)
          : isTemplate ?
              this.leagueEventsService.deleteEventTemplate(this.league!.id!, id, onSuccess, onError) :
              this.leagueEventsService.removeEventFromLeague(id, this.league!.id!, onSuccess, onError);
    }, () => {});
  }

  openChangeTypeDialog(event: EventItem, isTemplate: boolean) {
    if (this.disableEditing || !event.id || !this.league?.id) return;
    const type = event.leagueEventType === LeagueEventType.MANDATORY ? LeagueEventType.BONUS : LeagueEventType.MANDATORY;

    const modalRef = this.modalService.open(ConfirmationModalComponent, { centered: true });
    modalRef.componentInstance.title = this.translocoService.translate('league.details.events.change.event.type.modal.title');
    modalRef.componentInstance.text = this.translocoService.translate('league.details.events.change.event.type.modal.text.' + type );

    modalRef.result.then(() => {
      this.loaderService.startLoading();
      const successKey = 'league.details.events.change.event.type.success';
      const errorKey = 'league.details.events.change.event.type.error';

      const onSuccess = () => this.refreshLeagueEvents(successKey);
      const onError = () => this.handleError(errorKey);

      this.leagueEventsService.updateEventType(event.id!, this.league!.id!, isTemplate, type, onSuccess, onError);
    }, () => {});
  }

  openShortNameModal(eventItem: EventItem) {
    const modalRef = this.modalService.open(EventShortNameModalComponent, { centered: true, size: 'lg' });
    modalRef.componentInstance.event = eventItem;
    modalRef.componentInstance.title = this.translocoService.translate('event.item.add.name.short');

    modalRef.componentInstance.onSave.subscribe((shortName: MultiLangField) => {
      this.loaderService.startLoading()
      const successKey = 'league.details.events.add.short.name.success';
      const errorKey = 'league.details.events.add.short.name.error';

      const onSuccess = () => {
        eventItem.nameShort = shortName;
        if (this.leagueEvents?.events) {
          this.leagueEvents.events = this.leagueEvents.events.map(e =>
              e.id === eventItem.id ? { ...e, nameShort: shortName } : e
          );
        }
        this.loaderService.dismissLoading();
        this.toastService.showToast(TOAST_STATE.success, this.translocoService.translate(successKey));
      };
      const onError = () => this.handleError(errorKey);

      this.leagueEventsService.updateLeagueEventShortName(eventItem.id!, this.league!.id!, shortName, onSuccess, onError);
    });
  }

  protected readonly LeagueEventTableType = LeagueEventTableType;
  protected readonly LeagueEventType = LeagueEventType;

}
